import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { nvStorage } from '@simwood/nv-react-components';
import { nvFetch } from '../helpers/nvFetch';
import { isBridgeApp, isElectron } from '../helpers/platform';
import * as platform from '../helpers/platformApi';
import { completelyFetchResource } from '../helpers/resources';
import type { ThunkAPI } from '../store';
import { fetchExtensionDetails, finaliseLogout } from '../store/thunks';
import { ApiCustomer } from '../types/Customer';
import { ApiLinkedUser } from '../types/LinkedUser';
import { ApiOutgoingCallerId } from '../types/OutgoingCallerId';
import {
  ApiPhoneExtension,
  ApiPhoneExtensionWithSip,
} from '../types/PhoneExtension';
import { editPhoneExtension } from './contacts';

type ExtensionMenu = 'extension' | 'voicemail' | 'forwardingRules';
export interface ClientState {
  darkMode: boolean;
  visibleExtensionMenu?: ExtensionMenu;
  activeCustomer?: ApiCustomer;
  customers: ApiCustomer[];
  isFetchingCustomers: boolean;
  isFetchingLinkedUsers: boolean;
  isFetchingOutgoingCallerIds: boolean;
  isFetchingExtension: boolean;
  linkedUser?: ApiLinkedUser;
  linkedUsersList: ApiLinkedUser[];
  linkedUsersMap?: Record<string, string>;
  outgoingCallerIds?: ApiOutgoingCallerId[];
  selectedCallerId?: ApiOutgoingCallerId;
  extension?: ApiPhoneExtensionWithSip;
  error?: SerializedError;
}

const initialState: ClientState = {
  darkMode: false,
  visibleExtensionMenu: undefined,
  activeCustomer: undefined,
  customers: [],
  isFetchingCustomers: false,
  isFetchingLinkedUsers: false,
  isFetchingExtension: false,
  isFetchingOutgoingCallerIds: false,
  linkedUsersList: [],
  selectedCallerId: undefined,
  error: undefined,
};

const maybeSetInitialCallerId = (state: ClientState) => {
  if (!state.extension?.defaultCallerId || state.selectedCallerId?.id) {
    return;
  }

  const initialCallerId = state.outgoingCallerIds?.find(
    (x) => x.id === state.extension!.defaultCallerId,
  );

  if (initialCallerId) {
    state.selectedCallerId = initialCallerId;
  }
};

export const fetchLinkedUsers = createAsyncThunk<void, void, ThunkAPI>(
  'client/fetchLinkedUsers',
  async (_, thunkAPI) => {
    const {
      client: { isFetchingLinkedUsers, activeCustomer },
    } = thunkAPI.getState();

    if (isFetchingLinkedUsers || !activeCustomer) {
      return;
    }

    thunkAPI.dispatch(linkedUsersFetching());

    const fetchLinkedUsers = async () => {
      const fetchResult = await nvFetch(
        `/customers/${activeCustomer.id}/linkedusers`,
      );
      return (await fetchResult.json()).items as ApiLinkedUser[];
    };

    const fetchMe = async () => {
      const fetchResult = await nvFetch(
        `/customers/${activeCustomer.id}/linkedusers/me`,
      );
      return (await fetchResult.json()) as ApiLinkedUser;
    };

    const [linkedUsersList, linkedUser] = await Promise.all([
      fetchLinkedUsers(),
      fetchMe(),
    ]);
    thunkAPI.dispatch(linkedUsersFetched({ linkedUsersList, linkedUser }));

    if (linkedUser.defaultExtension) {
      thunkAPI.dispatch(fetchExtensionDetails(linkedUser.defaultExtension));
    }
  },
);

export const setActiveCustomerAndFetchLinkedUsers = createAsyncThunk<
  void,
  string,
  ThunkAPI
>(
  'client/setActiveCustomerAndFetchLinkedUsers',
  async (customerId, thunkAPI) => {
    platform.hasSelectedTeam(customerId);
    const activeCustomer = thunkAPI
      .getState()
      .client.customers.find((c) => c.id === customerId);
    nvStorage.set('selectedCustomerId', customerId);
    // @ts-expect-error
    thunkAPI.dispatch(setActiveCustomer(activeCustomer));
    thunkAPI.dispatch(fetchLinkedUsers());
    thunkAPI.dispatch(fetchOutgoingCallerIds());
  },
);

export const fetchCustomers = createAsyncThunk<void, void, ThunkAPI>(
  'client/fetchCustomers',
  async (_, thunkAPI) => {
    const {
      client: { isFetchingCustomers },
      user: { accessToken, user },
    } = thunkAPI.getState();

    if (isFetchingCustomers) {
      return;
    }

    thunkAPI.dispatch(customersFetching());
    const fetchResult = await nvFetch(`/customers`);
    const customers = (await fetchResult.json()).items as ApiCustomer[];

    thunkAPI.dispatch(customersFetched(customers));
    // console.log('FETCHED', res);
    platform.hasLoggedIn(
      accessToken!,
      user!,
      customers.map((entry) => ({
        name: entry.company,
        id: entry.id,
      })),
    );
    let selectedCustomerId;

    if (customers.length === 1) {
      selectedCustomerId = customers[0].id;
    } else if (isElectron() && customers.length > 1) {
      const savedSelectedCustomer = nvStorage.get('selectedCustomerId');
      const customer = customers.find((c) => c.id === savedSelectedCustomer);
      if (customer) {
        selectedCustomerId = customer.id;
      } else if (isBridgeApp()) {
        selectedCustomerId = customers[0].id;
      }
    }

    if (selectedCustomerId) {
      thunkAPI.dispatch(
        setActiveCustomerAndFetchLinkedUsers(selectedCustomerId),
      );
    }
  },
);

export const fetchOutgoingCallerIds = createAsyncThunk<void, void, ThunkAPI>(
  'client/fetchOutgoingCallerIds',
  async (_, thunkAPI) => {
    const {
      client: { isFetchingOutgoingCallerIds, activeCustomer },
    } = thunkAPI.getState();

    if (isFetchingOutgoingCallerIds || !activeCustomer) {
      return;
    }

    thunkAPI.dispatch(outgoingCallerIdsFetching());

    const outgoingCallerIds =
      await completelyFetchResource<ApiOutgoingCallerId>(
        `outgoingcallerids`,
        '',
        { pageSize: 200 },
        activeCustomer.id,
      );

    thunkAPI.dispatch(outgoingCallerIdsFetched({ outgoingCallerIds }));

    // nvFetch(`/customers/${activeCustomerId}/outgoingcallerids`, {
    //   params: {
    //     pageSize: 200,
    //   },
    // })
    //   .then((res) => res.json())
    //   .then((data: { items: ApiOutgoingCallerId[] }) => {
    //     if (data.items) {
    //       setOutgoingCallerIds(data.items);

    //       if (!selectedCallerId?.id && defaultCallerId) {
    //         const initialCallerId = data.items.find(
    //           (x) => x.id === defaultCallerId,
    //         );
    //         if (initialCallerId) {
    //           dispatch(setCallerId(initialCallerId));
    //         }
    //       }
    //     }
    //   });
  },
);

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    setVisibleExtensionMenu: (
      state,
      action: PayloadAction<ExtensionMenu | undefined>,
    ) => {
      state.visibleExtensionMenu = action.payload;
    },
    customersFetching: (state) => {
      state.isFetchingCustomers = true;
    },
    customersFetched: (state, action: PayloadAction<ApiCustomer[]>) => {
      state.isFetchingCustomers = false;
      state.error = undefined;
      state.customers = action.payload;
    },
    setActiveCustomer: (state, action: PayloadAction<ApiCustomer>) => {
      state.activeCustomer = action.payload;
    },
    linkedUsersFetching: (state) => {
      state.isFetchingLinkedUsers = true;
    },
    linkedUsersFetched: (
      state,
      action: PayloadAction<{
        linkedUsersList: ApiLinkedUser[];
        linkedUser: ApiLinkedUser;
      }>,
    ) => {
      const { linkedUsersList, linkedUser } = action.payload;
      const map: Record<string, string> = {};
      linkedUsersList.forEach((x) => {
        if (x.defaultExtension && !map[x.defaultExtension]) {
          map[x.defaultExtension] = x.email;
        }
      });
      state.isFetchingLinkedUsers = false;
      state.error = undefined;
      state.linkedUsersList = linkedUsersList;
      state.linkedUser = linkedUser;
      state.linkedUsersMap = map;
    },
    outgoingCallerIdsFetching: (state) => {
      state.isFetchingOutgoingCallerIds = true;
    },
    outgoingCallerIdsFetched: (
      state,
      action: PayloadAction<{
        outgoingCallerIds: ApiOutgoingCallerId[];
      }>,
    ) => {
      const { outgoingCallerIds } = action.payload;
      state.outgoingCallerIds = outgoingCallerIds;

      maybeSetInitialCallerId(state);
    },
    enableDarkMode: (state) => {
      state.darkMode = true;
    },
    disableDarkMode: (state) => {
      state.darkMode = false;
    },
    setCallerId: (state, action: PayloadAction<ApiOutgoingCallerId>) => {
      state.selectedCallerId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setActiveCustomerAndFetchLinkedUsers.pending, (state) => {
        state.extension = undefined;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isFetchingCustomers = false;
        state.error = action.error;
      })
      .addCase(fetchLinkedUsers.rejected, (state, action) => {
        state.isFetchingLinkedUsers = false;
        state.error = action.error;
      })
      .addCase(fetchExtensionDetails.pending, (state) => {
        state.isFetchingExtension = true;
      })
      .addCase(fetchExtensionDetails.rejected, (state, action) => {
        state.isFetchingExtension = false;
        state.error = action.error;
      })
      .addCase(
        fetchExtensionDetails.fulfilled,
        (state, action: PayloadAction<ApiPhoneExtensionWithSip>) => {
          platform.hasSelectedExtension(action.payload);

          state.extension = action.payload;
          state.isFetchingExtension = false;
          state.error = undefined;

          maybeSetInitialCallerId(state);
        },
      )
      // FIXME the current extension should be derived/selected from the id and array of all extensions
      // instead of saving it as an object. The problem is currently we fetch this extension separately before
      // fetching anything else
      .addCase(
        editPhoneExtension.fulfilled,
        (state, action: PayloadAction<ApiPhoneExtension | undefined>) => {
          const entry = action.payload;

          if (!entry || !state.extension || state.extension.id !== entry.id) {
            return;
          }

          state.extension = { ...state.extension, ...entry };
          if (!entry.defaultCallerId) {
            state.extension.defaultCallerId = null;
          }
        },
      )
      .addCase(finaliseLogout.fulfilled, () => initialState);
  },
});

export const {
  setVisibleExtensionMenu,
  customersFetching,
  customersFetched,
  setActiveCustomer,
  linkedUsersFetching,
  linkedUsersFetched,
  outgoingCallerIdsFetching,
  outgoingCallerIdsFetched,
  enableDarkMode,
  disableDarkMode,
  setCallerId,
} = clientSlice.actions;

export default clientSlice.reducer;
